<template>
  <span>
    <el-popover
      v-if="isPopoverMode"
      @show="popoverOpen = true"
      @hide="popoverOpen = false"
      :placement="popoverPlacement"
      :trigger="popoverTrigger"
      :width="activeInteractionConfig.popoverWidth || 400"
      :popper-style="popoverStyle"
      :popper-class="popoverClass"
    >
      <template #reference>
        <span class="image-item-wrap image-item-wrap--interactive"
          :class="floatingMenuClasses"
          :tabindex="floatingMenuVisual ? 0 : undefined"
          :role="floatingMenuVisual ? 'button' : undefined"
          :aria-label="floatingMenuLabel || undefined"
          :aria-expanded="floatingMenuVisual ? popoverOpen : undefined"
          @keydown="handleFloatingMenuKeydown"
        >
          <span
            class="image-item-media"
            :class="{ 'image-item-media--hoverable': hoverSrc }"
          >
            <LazyImage
              class="image-item image-item--default"
              :src="showSrc"
              :fit="config.fit"
              :use-lazy="config.useLazy !== false"
              :tint-color="imageTintColor"
            />
            <LazyImage
              v-if="hoverSrc"
              class="image-item image-item--hover"
              :src="hoverSrc"
              :fit="config.fit"
              :use-lazy="config.useLazy !== false"
              :tint-color="imageTintColor"
            />
          </span>
          <span v-if="floatingMenuVisual && !floatingMenuVisual.hideLabel" class="floating-menu-label">{{ floatingMenuLabel }}</span>
        </span>
      </template>
      <PopoverPageRender
        :area-data="activeInteractionConfig.popoverPageData"
        :width="activeInteractionConfig.popoverWidth || 400"
        :background-color="activeInteractionConfig.popoverBackgroundColor || '#ffffff'"
        :border-radius="getPopoverBorderRadius(activeInteractionConfig)"
      />
    </el-popover>

    <component
      v-else
      :is="safeLink ? 'a' : 'div'"
      :target="jumpWay"
      :href="safeLink || undefined"
      class="image-item-wrap"
      :class="floatingMenuClasses"
      :tabindex="floatingMenuVisual && !safeLink ? 0 : undefined"
      :role="floatingMenuVisual && !safeLink ? 'button' : undefined"
      :aria-label="floatingMenuLabel || undefined"
      @keydown="handleFloatingMenuKeydown"
      @click="imageClickFn"
    >
      <span
        class="image-item-media"
        :class="{ 'image-item-media--hoverable': hoverSrc }"
      >
        <LazyImage
          class="image-item image-item--default"
          :src="showSrc"
          :fit="config.fit"
          :use-lazy="config.useLazy !== false"
          :tint-color="imageTintColor"
        />
        <LazyImage
          v-if="hoverSrc"
          class="image-item image-item--hover"
          :src="hoverSrc"
          :fit="config.fit"
          :use-lazy="config.useLazy !== false"
          :tint-color="imageTintColor"
        />
      </span>
      <span v-if="floatingMenuVisual && !floatingMenuVisual.hideLabel" class="floating-menu-label">{{ floatingMenuLabel }}</span>
    </component>

    <el-dialog
      v-if="activeInteractionConfig && activeInteractionConfig.responseType === 'popup'"
      :class="activeInteractionConfig.popupClass || ''"
      :title="activeInteractionConfig.popupTitle || ''"
      append-to-body
      v-model="dialogTableVisible"
    >
      <template #default>
        <div
          v-if="dialogTableVisible && articleContent && articleContent.content"
          class="new-detail-item-cont"
          v-html="articleContent.content"
        ></div>
      </template>
      <template #footer>
        <el-button type="primary" @click="dialogTableVisible = false">
          {{ getLocaleText('关闭') }}
        </el-button>
      </template>
    </el-dialog>
  </span>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted } from 'vue';
import { getArticleDetails } from "@/api";
import useLocate from './uses/useLocate.js';
import LazyImage from "@/components/common/LazyImage/index.vue";
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import { getSafeLink, isHttpLink, isTarget_Blank } from '@/utils/globalData.js';
import { useRenderConfig } from './uses/useRenderConfig.js';
import { resolveBindDataKeyValue } from '@/utils/renderBinding';
import { useLowCodeActionRuntime } from '@/composables/useLowCodeActionRuntime.js';
import { isLowCodePopupPageAction } from '@/utils/lowCodePopupPage.js';

const { getLocaleText } = useLocate();
const { t } = useI18n();
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject('getConfigJson', () => ({}));

const props = defineProps({
  floatingMenuVisual: { type: Object, default: null },
  currentElementIndex: {
    type: Array,
    default: () => [],
  },
  idx: {
    type: Number,
    default: -1,
  },
  parentKeys: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: true,
  },
  config: {
    type: Object,
  },
});

const dialogTableVisible = ref(false);
const popoverOpen = ref(false);
const floatingMenuLabel = computed(() => props.floatingMenuVisual
  ? t(props.floatingMenuVisual.labelKey) : '');
const floatingMenuClasses = computed(() => ({
  'floating-menu-item': Boolean(props.floatingMenuVisual),
  'floating-menu-item--open': Boolean(props.floatingMenuVisual) && (popoverOpen.value || dialogTableVisible.value),
  'floating-menu-item--close': props.floatingMenuVisual?.size === 20,
}));
function handleFloatingMenuKeydown(event) {
  if (!props.floatingMenuVisual || !['Enter', ' '].includes(event.key)) return;
  if (event.key === 'Enter' && event.currentTarget.tagName === 'A') return;
  event.preventDefault();
  event.currentTarget.click();
}
const articleContent = ref(null);
const { bindData } = useRenderConfig(() => props.config);

const showSrc = computed(() => {
  if (props.floatingMenuVisual) return props.floatingMenuVisual.src;
  return resolveBindDataKeyValue(
    bindData.value,
    props.config?.bindDataKey,
    props.config?.src || '',
  );
});

const hoverSrc = computed(() => props.floatingMenuVisual
  ? props.floatingMenuVisual.activeSrc || '' : props.config?.hoverSrc || '');

const imageTintColor = computed(() => {
  if (props.floatingMenuVisual) return '';
  return (
    props.config?.tintColor ||
    props.config?.imageColor ||
    props.config?.color ||
    ''
  );
});

const hasMeaningfulClickConfig = (config) => {
  if (isLowCodePopupPageAction(config)) {
    return true;
  }
  if (!config || !config.responseType) {
    return false;
  }
  if (config.responseType === 'link' || config.responseType === 'jumpLink') {
    return !!getSafeLink(config.link);
  }
  if (config.responseType === 'popup') {
    return !!config.article || !!config.popupTitle || !!config.popupClass;
  }
  if (config.responseType === 'popoverPage') {
    return (
      !!config.popoverPageId ||
      !!config.popoverPageTitle ||
      !!config.popoverPageData
    );
  }
  return true;
};

const activeInteractionConfig = computed(() => {
  const candidates = [
    props.config?.clickConfig,
    props.config?.clickAction,
    props.config?.link,
  ];
  return candidates.find((item) => isLowCodePopupPageAction(item)) ||
    (hasMeaningfulClickConfig(props.config?.clickConfig)
      ? props.config.clickConfig
      : null);
});

const isPopoverMode = computed(() => {
  return (
    activeInteractionConfig.value &&
    activeInteractionConfig.value.responseType === 'popoverPage' &&
    activeInteractionConfig.value.popoverPageData
  );
});

const popoverPlacement = computed(() => {
  return activeInteractionConfig.value?.popoverPlacement || 'right';
});

const popoverTrigger = computed(() => {
  return activeInteractionConfig.value?.popoverTrigger || 'hover';
});

const popoverClass = computed(() => {
  return ['image-item-popover', activeInteractionConfig.value?.popupClass || ''].filter(Boolean).join(' ');
});

const popoverStyle = computed(() => {
  return {
    padding: '0px',
    border:'none',
    overflow: 'hidden',
    borderRadius: `${getPopoverBorderRadius(activeInteractionConfig.value)}px`,
    backgroundColor:
      activeInteractionConfig.value?.popoverBackgroundColor || '#ffffff',
  };
});

function getPopoverBorderRadius(config) {
  return typeof config?.popoverBorderRadius === 'number'
    ? config.popoverBorderRadius
    : 6;
}

const safeLink = computed(() => {
  const interactionConfig = activeInteractionConfig.value;
  if (interactionConfig) {
    if (!['link', 'jumpLink'].includes(interactionConfig.responseType || '')) {
      return '';
    }
    return getSafeLink(interactionConfig.link);
  }
  return getSafeLink(props.config?.link);
});

const jumpWay = computed(() => {
  const interactionConfig = activeInteractionConfig.value;
  if (interactionConfig) {
    return interactionConfig.responseType === 'jumpLink'
      ? isTarget_Blank()
      : '_Self';
  }
  return isHttpLink(props.config?.link) ? isTarget_Blank() : '_Self';
});

const getArticleContent = async () => {
  const interactionConfig = activeInteractionConfig.value;
  if (
    interactionConfig?.responseType !== 'popup' ||
    !interactionConfig?.article?.id
  ) {
    articleContent.value = null;
    return;
  }
  const param = {
    pageNo: 0,
    pageSize: 9999,
    id: interactionConfig.article.id,
    status: "1",
  };
  const res = await getArticleDetails(param);
  if (res.data.dataList) {
    articleContent.value = res.data.dataList[0];
    if (articleContent.value.type === 1 && articleContent.value.content) {
      articleContent.value.content = articleContent.value.content.replace(
        'video',
        'video autoplay '
      );
    }
  } else {
    articleContent.value = null;
  }
};

watch(
  () => activeInteractionConfig.value?.article,
  (value) => {
    if (value || activeInteractionConfig.value?.responseType === 'popup') {
      getArticleContent();
    }
  },
  { deep: true }
);

watch(
  () => activeInteractionConfig.value?.responseType,
  (value) => {
    if (value !== 'popup') {
      dialogTableVisible.value = false;
      articleContent.value = null;
      return;
    }
    getArticleContent();
  },
  { immediate: true }
);

onMounted(() => {
  getArticleContent();
});

const imageClickFn = (event) => {
  if (isLowCodePopupPageAction(activeInteractionConfig.value)) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dispatchLowCodeAction(
      activeInteractionConfig.value,
      typeof getConfigJson === 'function' ? getConfigJson() : {},
    );
    return;
  }
  if (activeInteractionConfig.value?.responseType !== 'popup') {
    return;
  }
  event?.preventDefault?.();
  dialogTableVisible.value = true;
};
</script>

<style scoped>
.floating-menu-item.image-item-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 48px;
  height: auto;
  min-height: 48px;
  padding: 10px 6px;
  border-radius: 8px;
  color: #18181b;
  text-decoration: none;
  cursor: pointer;
}
.floating-menu-item .image-item-media {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  flex: none;
}
.floating-menu-item--close .image-item {
  width: 20px;
  height: 20px;
  margin: 2px;
}
.floating-menu-label {
  display: none;
  width: 48px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}
.floating-menu-item:is(:hover, :focus-visible, .floating-menu-item--open) .floating-menu-label {
  display: block;
}
.floating-menu-item:is(:hover, :focus-visible, .floating-menu-item--open) .image-item-media--hoverable .image-item--default {
  opacity: 0;
}
.floating-menu-item:is(:hover, :focus-visible, .floating-menu-item--open) .image-item--hover {
  opacity: 1;
}
.floating-menu-item:focus-visible {
  outline: 2px solid var(--el-color-primary-light-7, #ffddc2);
  outline-offset: -2px;
}
.image-item-wrap {
  display: block;
  width: 100%;
  height: 100%;
}

.image-item-wrap--interactive {
  cursor: pointer;
}

.image-item {
  width: 100%;
  height: 100%;
  display: block;
  transition: opacity 300ms ease;
}

.image-item-media {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.image-item--hover {
  position: absolute;
  inset: 0;
  opacity: 0;
}

@media (hover: hover) {
  .image-item-media--hoverable:hover .image-item--default {
    opacity: 0;
  }

  .image-item-media--hoverable:hover .image-item--hover {
    opacity: 1;
  }
}
</style>

<style>
.image-item-popover.el-popover {
  padding: 0 !important;
}
</style>
